/** Hour parsing/formatting for timesheet cells and the timer. */

/**
 * Parse a cell input in either Harvest format:
 *   "1:30" → 1.5   ":45" → 0.75   "1.5" → 1.5   "2" → 2   "" → 0
 * Returns null when the text is not a valid duration.
 */
export function parseHoursInput(raw: string): number | null {
  const text = raw.trim();
  if (text === '') return 0;
  const colon = text.match(/^(\d{0,3}):([0-5]?\d)$/);
  if (colon) {
    const h = colon[1] === '' ? 0 : parseInt(colon[1], 10);
    const m = parseInt(colon[2], 10);
    return Math.round((h + m / 60) * 100) / 100;
  }
  const decimal = text.match(/^(\d{0,3})(?:[.,](\d{0,4}))?$/);
  if (decimal && text !== '.' && text !== ',') {
    const value = parseFloat(text.replace(',', '.'));
    if (Number.isFinite(value)) return Math.round(value * 100) / 100;
  }
  return null;
}

/**
 * How durations render across the timesheet. Financial software lines its
 * decimals up, so `decimal` is always two places (5.00 / 2.75); `hm` mirrors
 * the H:MM the cell parser already accepts (5:00 / 2:45).
 */
export type HoursFormat = 'decimal' | 'hm';

const decimalFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Fixed-width duration: 5.00 / 2.75 or 5:00 / 2:45. Never blank. */
export function formatHoursValue(hours: number, format: HoursFormat = 'decimal'): string {
  const safe = Number.isFinite(hours) ? hours : 0;
  if (format === 'hm') {
    const totalMinutes = Math.round(Math.abs(safe) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${safe < 0 ? '-' : ''}${h}:${String(m).padStart(2, '0')}`;
  }
  return decimalFormatter.format(Math.round(safe * 100) / 100);
}

/**
 * A duration in running prose, in whatever unit the sheet is currently showing:
 * `13.00 h` in decimal, `13:00` in hh:mm.
 *
 * The app's one hours unit is `h` (`formatHours(n, {unit:true})`), but that
 * formatter only speaks decimal — so the draft status bar read "16.5 h tracked"
 * directly above a grid reading 16:30, and the submit dialog said "13 h" over a
 * week the user had switched to hh:mm. Two formats for one number on one screen
 * is exactly what financial software must not do. `hh:mm` carries its own unit
 * in the colon, so no suffix is appended there.
 */
export function formatHoursUnit(hours: number, format: HoursFormat = 'decimal'): string {
  return format === 'hm' ? formatHoursValue(hours, 'hm') : `${formatHoursValue(hours, 'decimal')} h`;
}

/** Cell display: 6.50, 3.25, 0.01 (or 6:30, 3:15) — empty string for 0. */
export function formatCellHours(hours: number, format: HoursFormat = 'decimal'): string {
  if (!hours) return '';
  return formatHoursValue(hours, format);
}

/** Timer elapsed as H:MM:SS. */
export function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/** Monday (ISO date) of the week containing an ISO date. */
export function mondayOf(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  const shift = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - shift);
  return date.toISOString().slice(0, 10);
}

export function addDaysIso(isoDate: string, days: number): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Today's local calendar date as ISO. */
export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** "Mon" for an ISO date (weeks always start Monday). */
export function weekdayLabel(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00Z`);
  return WEEKDAYS[(date.getUTCDay() + 6) % 7];
}

/** "Aug 10 – 16, 2026" or "Aug 31 – Sep 6, 2026" for a week. */
export function weekRangeLabel(weekStart: string): string {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const end = new Date(`${addDaysIso(weekStart, 6)}T00:00:00Z`);
  const month = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  const endPart = sameMonth ? `${end.getUTCDate()}` : `${month(end)} ${end.getUTCDate()}`;
  return `${month(start)} ${start.getUTCDate()} – ${endPart}, ${end.getUTCFullYear()}`;
}
